import consumer from "./consumer"

// Service Queue Channel for real-time dashboard updates
// Usage: Call ServiceQueueChannel.subscribe(branchId) to start receiving updates

const ServiceQueueChannel = {
  subscription: null,
  branchId: null,
  
  subscribe(branchId) {
    // Unsubscribe from previous if exists
    if (this.subscription) {
      this.subscription.unsubscribe()
    }
    
    this.branchId = branchId
    
    this.subscription = consumer.subscriptions.create(
      { channel: "ServiceQueueChannel", branch_id: branchId },
      {
        connected() {
          console.log(`ServiceQueueChannel: Connected to branch ${branchId || 'all'}`)
          
          // Update connection indicator (check both possible IDs)
          const indicator = document.getElementById('realtime-indicator') || document.getElementById('connection-indicator')
          if (indicator) {
            const dot = indicator.querySelector('.indicator-dot')
            const text = indicator.querySelector('.indicator-text')
            if (dot) {
              dot.classList.remove('bg-gray-400', 'bg-yellow-500', 'bg-red-500', 'animate-pulse')
              dot.classList.add('bg-green-500')
            }
            if (text) {
              text.textContent = 'Live'
              text.classList.remove('text-gray-500', 'text-yellow-600', 'text-red-500', 'text-red-600')
              text.classList.add('text-green-600')
            }
            indicator.classList.remove('bg-gray-200', 'bg-yellow-100', 'bg-red-100')
            indicator.classList.add('bg-green-100')
            indicator.title = 'Real-time updates active'
          }
          
          // Dispatch custom event for other listeners
          document.dispatchEvent(new CustomEvent('serviceQueueConnected'))
        },

        disconnected() {
          console.log('ServiceQueueChannel: Disconnected')
          
          // Update connection indicator (check both possible IDs)
          const indicator = document.getElementById('realtime-indicator') || document.getElementById('connection-indicator')
          if (indicator) {
            const dot = indicator.querySelector('.indicator-dot')
            const text = indicator.querySelector('.indicator-text')
            if (dot) {
              dot.classList.remove('bg-gray-400', 'bg-yellow-500', 'bg-green-500')
              dot.classList.add('bg-red-500', 'animate-pulse')
            }
            if (text) {
              text.textContent = 'Reconnecting...'
              text.classList.remove('text-gray-500', 'text-yellow-600', 'text-green-600')
              text.classList.add('text-red-600')
            }
            indicator.classList.remove('bg-gray-200', 'bg-yellow-100', 'bg-green-100')
            indicator.classList.add('bg-red-100')
            indicator.title = 'Real-time updates disconnected - will retry'
          }
          
          // Dispatch custom event for other listeners
          document.dispatchEvent(new CustomEvent('serviceQueueDisconnected'))
        },

        received(data) {
          console.log('ServiceQueueChannel: Received', data)
          
          if (data.action === 'update') {
            ServiceQueueChannel.handleUpdate(data)
          }
        },
        
        // Send status update from technician/advisor interface
        updateStatus(queueId, statusCode, userId) {
          this.perform('update_status', {
            queue_id: queueId,
            status_code: statusCode,
            user_id: userId
          })
        },
        
        // Advance to next status
        advanceStatus(queueId, userId) {
          this.perform('update_status', {
            queue_id: queueId,
            advance: true,
            user_id: userId
          })
        },
        
        // Revert to previous status
        revertStatus(queueId, userId) {
          this.perform('update_status', {
            queue_id: queueId,
            revert: true,
            user_id: userId
          })
        }
      }
    )
    
    return this.subscription
  },
  
  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  },
  
  // Handle incoming queue updates
  handleUpdate(data) {
    const queueCard = document.getElementById(`queue_${data.queue_id}`)
    
    if (queueCard) {
      // Check if status changed - if so, card may need to move to different column
      const currentStatus = queueCard.dataset.status
      if (currentStatus && currentStatus !== data.status) {
        // Status changed, reload to move card to correct column
        console.log(`ServiceQueueChannel: Status changed from ${currentStatus} to ${data.status}, reloading...`)
        this.scheduleReload()
        return
      }
      
      // Update the card's classes based on alert status
      queueCard.className = `${data.alert_class} rounded-lg p-4 border-l-4`
      queueCard.dataset.status = data.status
      
      // Update status badge
      const statusBadge = queueCard.querySelector('.status-badge')
      if (statusBadge) {
        statusBadge.textContent = data.status_name
        statusBadge.className = `status-badge px-2 py-1 text-xs font-medium rounded ${data.alert_class}`
      }
      
      // Update progress bar if exists
      const progressBar = queueCard.querySelector('.progress-bar')
      if (progressBar) {
        progressBar.style.width = `${data.progress_percentage}%`
      }
      
      // Update time display
      const timeDisplay = queueCard.querySelector('.time-display')
      if (timeDisplay && data.time_elapsed_minutes !== null) {
        const hours = Math.floor(data.time_elapsed_minutes / 60)
        const minutes = data.time_elapsed_minutes % 60
        timeDisplay.textContent = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`
      }
      
      // Flash the card to indicate update
      queueCard.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50')
      setTimeout(() => {
        queueCard.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50')
      }, 2000)
    } else {
      // NEW queue entry - reload to show it
      console.log(`ServiceQueueChannel: New queue ${data.queue_id} detected, reloading...`)
      this.scheduleReload()
    }
    
    // Trigger custom event for any additional handlers
    document.dispatchEvent(new CustomEvent('serviceQueueUpdated', { detail: data }))
  },
  
  // Schedule a reload with debounce to avoid multiple rapid reloads
  reloadTimeout: null,
  scheduleReload() {
    if (this.reloadTimeout) {
      clearTimeout(this.reloadTimeout)
    }
    // Debounce reload by 500ms to batch multiple rapid updates
    this.reloadTimeout = setTimeout(() => {
      location.reload()
    }, 500)
  }
}

// Export for use in views
window.ServiceQueueChannel = ServiceQueueChannel

export default ServiceQueueChannel
